import {useState, useEffect, useRef} from 'react';

import type {Props, ExponeaArtworkRecommendation} from './typings';

import useExponea from '@/hooks/use-exponea/use-exponea';

const EXPONEA_SDK_TIMEOUT_MS = 1000;

interface FetchedDataState {
  data: ExponeaArtworkRecommendation[];
  isLoading: boolean;
  error: null | string;
}

/**
 * @descriptionCustom hook to fetch recommendations from Exponea.
 * It tracks user interactions with the recommendations.
 * @param {object} params - parameters for fetching recommendations.
 * @param {string|undefined} params.recommendationId - (optional) the recommendation ID (see bloomreach dashboard).
 * @param {string} params.userEmail - email of the user for tracking purposes.
 * @param {number|undefined} params.size - (optional, default=4) number of items to fetch.
 * @param {'in'|'cm'|undefined} params.displaySizeUnits - (optional) size units.
 * @return {object} An object containing the recommendations, status, and a function to track item clicks.
 */
const useExponeaRecommendations = ({
  recommendationId,
  userEmail,
  size = 4,
  displaySizeUnits,
}: Props) => {
  const {exponea} = useExponea();
  const [exponeaData, setExponeaData] = useState<FetchedDataState>({
    data: [],
    isLoading: false,
    error: null,
  });

  const {data, isLoading, error} = exponeaData;

  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleTrackItemClick = (itemId: string | number) => {
    exponea?.track('recommendation', {
      action: 'click',
      item_id: itemId,
      recommendation_id: recommendationId,
    });
  };

  /**
   * @description Fetches recommendations from Exponea.
   * - prevents fetching if `exponea` or `recommendationId` is not available.
   */
  useEffect(() => {
    if (!exponea || !recommendationId) return;
    setExponeaData((current) => ({...current, status: 'loading'}));

    const options = {
      recommendationId,
      size,
      fillWithRandom: true,
      callback: (receivedData: ExponeaArtworkRecommendation[] | null) => {
        setExponeaData((current) => {
          if (current.error === 'timeout') {
            return current;
          }
          return {
            error: null,
            data: receivedData ?? [],
            isLoading: false,
          };
        });
      },
    };

    exponea.getRecommendation(options);

    timeoutRef.current = setTimeout(() => {
      setExponeaData((current) => {
        if (current.data.length === 0) {
          exponea?.track('recommendation', {
            action: 'timeout',
            recommendation_id: recommendationId,
          });
          return {error: 'timeout', data: [], isLoading: false};
        }
        return current;
      });
    }, EXPONEA_SDK_TIMEOUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [recommendationId, exponea]);

  useEffect(() => {
    if (!userEmail || !exponea?.identify) return;

    exponea.identify({
      register: userEmail,
    });
  }, [userEmail, exponea?.identify]);

  return {
    data,
    isLoading,
    error,
    handleTrackItemClick,
  };
};

export default useExponeaRecommendations;
